import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  forwardRef,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core'
import { SafeStyle, DomSanitizer } from '@angular/platform-browser'
import { IcosInputPoprs } from './input-props'
import { getTextareaHeight } from './help'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { isParentTag } from '../shared/utils'
import { IcosFormItem } from '../form/form-item'

@Component({
  selector: 'icos-input',
  styles: [
    `
      .icon-disabled {
        cursor: not-allowed;
      }
      .icon-disabled:before {
        cursor: not-allowed;
      }
      .icon-pointer {
        cursor: pointer;
      }
    `
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IcosInput),
      multi: true
    }
  ],
  template: `
    <div
      [class]="(type === 'text' ? 'icos-input' : 'icos-textarea') + (size ? ' icos-input--' + size : '') + ' ' + parentClass"
      [class.is-disabled]="icosDisabled"
      [class.icos-input-group]="prepend || append"
      [class.icos-input-group--append]="append"
      [class.icos-input-group--prepend]="prepend"
    >
      <ng-container *ngIf="type === 'text'">
        <div class="icos-input-group__prepend" *ngIf="prepend">
          <ng-template [ngTemplateOutlet]="prepend"> </ng-template>
        </div>
        <!--
          bugs #5758 icos-input  icon-class 属性不能用
          icos-input  icon-class 属性不能用，iconClass会生效但是必须加icon 才能加上class，但是加上icon ，又会和iconClass的图标冲突；
          最终图标无法正常显示。
          2019/6/24
        -->
        <span
          class="icos-input__suffix"
          *ngIf="icon || iconClass"
          (click)="iconClick.emit($event)"
          (mouseenter)="iconMouseEnter.emit($event)"
          (mouseleave)="iconMouseLeave.emit($event)"
          (mousedown)="iconMousedown.emit($event)"
          (mouseup)="iconMouseup.emit($event)"
          [class.icon-pointer]="showPointer()"
          [class.icon-disabled]="icosDisabled"
        >
          <span class="icos-input__suffix-inner">
            <i
              [class]="
                'icos-input__icon ' +
                ('icos-icon-' + icon) +
                (iconClick ? ' is-clickable ' : ' ') +
                (iconClass ? iconClass : '')
              "
              [attr.disabled]="icosDisabled"
              [class.icon-disabled]="icosDisabled"
              *ngIf="icon"
            ></i>
            <i
              [class]="'icos-input__icon ' + (iconClick ? ' is-clickable ' : ' ') + (iconClass ? iconClass : '')"
              [attr.disabled]="icosDisabled"
              [class.icon-disabled]="icosDisabled"
              *ngIf="iconClass"
            ></i>
          </span>
        </span>
        <input
          class="icos-input__inner"
          [autocomplete]="autoComplete"
          [value]="value"
          [name]="name"
          [type]="nativeType"
          [placeholder]="placeholder"
          [autofocus]="autofocus"
          [disabled]="icosDisabled"
          [readonly]="readonly"
          [maxlength]="maxlength"
          [minlength]="minlength"
          [ngModel]="model"
          (ngModelChange)="handleInput($event)"
          (focus)="focus.emit($event)"
          (blur)="blur.emit($event)"
        />
        <i *ngIf="validating" class="icos-input__icon icos-icon-loading"></i>

        <div class="icos-input-group__append" *ngIf="append">
          <ng-template [ngTemplateOutlet]="append"> </ng-template>
        </div>
      </ng-container>

      <ng-container *ngIf="type === 'textarea'">
        <textarea
          class="icos-textarea__inner"
          #textarea
          [style]="textareaStyles"
          [value]="value"
          [name]="name"
          [placeholder]="placeholder"
          [autofocus]="autofocus"
          [disabled]="icosDisabled"
          [readonly]="readonly"
          [maxlength]="maxlength"
          [minlength]="minlength"
          [ngModel]="model"
          (input)="handleInput($event.target.value)"
          (focus)="focus.emit($event)"
          (blur)="blur.emit($event)"
        ></textarea>
      </ng-container>
    </div>
  `
})
export class IcosInput extends IcosInputPoprs implements OnInit, AfterViewInit, ControlValueAccessor {
  @ContentChild('prepend') prepend: TemplateRef<any>
  @ContentChild('append') append: TemplateRef<any>
  @ViewChild('textarea') textarea: any
  textareaStyles: SafeStyle
  validating: false
  constructor(private sanitizer: DomSanitizer, private el: ElementRef, private form: IcosFormItem) {
    super()
  }

  makeTextareaStyles(): void {
    if (!this.autosize || this.type !== 'textarea') return
    const height: string = getTextareaHeight(this.textarea.nativeElement, this.autosize.minRows, this.autosize.maxRows)
    const styles: string = `resize: ${this.resize}; height: ${height};`
    this.textareaStyles = this.sanitizer.bypassSecurityTrustStyle(styles)
  }

  handleInput(val: string): void {
    this.model = val
    this.modelChange.emit(val)
    this.controlChange(val)
    const timer: any = setTimeout(() => {
      this.makeTextareaStyles()
      clearTimeout(timer)
    }, 0)
  }

  showPointer(): boolean {
    const clickEvent: boolean = !!(this.iconClick.observers && this.iconClick.observers.length)
    const mouseEvent: boolean = !!(this.iconMousedown.observers && this.iconMousedown.observers.length)

    return clickEvent || mouseEvent
  }

  ngOnInit(): void {
    // auto follow form status
    const parentIsForm: boolean = isParentTag(this.el.nativeElement, 'icos-form-item')
    if (parentIsForm) {
      const iconStatus = {
        error: 'circle-close',
        success: 'circle-check',
        validating: 'circle-validating'
      }
      this.iconClass = 'icos-input__validateIcon'
      this.form.statusSubscriber.push((status: string) => {
        this.icon = iconStatus[status] || ''
      })
    }

    if (this.value && !this.model) {
      this.model = this.value
    }
  }

  ngAfterViewInit(): any {
    // no content required
    if (this.type === 'textarea') {
      return setTimeout(() => {
        this.makeTextareaStyles()
      }, 0)
    }
  }

  writeValue(value: any): void {
    this.model = value
  }

  registerOnChange(fn: Function): void {
    this.controlChange = fn
  }

  registerOnTouched(fn: Function): void {
    this.controlTouch = fn
  }

  private controlChange: Function = () => {}
  private controlTouch: Function = () => {}
}
